import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { db as firestore, storage } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { FileStorageService } from '../../services/FileStorageService';
import { ContestEntryService } from '../../services/ContestEntryService';
import { ContestEntry } from '../../@types/contestEntry';
import { 
  PageContainer,
  MainContent,
  Card,
  Heading1,
  Heading2,
  Text,
  Button,
  Input,
  TextArea,
  FormGroup,
  FormLabel,
  FlexColumn,
  FlexRow,
  Spacer
} from '../../styles/StyledComponents';
import { ThemeColors, Spacing } from '../../styles/ThemeStyles';
import styled from 'styled-components';
import { Search, Store, MapPin, Trophy, Camera, Upload, X, CheckCircle, Sparkles, Plus, Calendar, Eye, Clock, Star, ChevronLeft, ChevronRight, Bell } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

const EncouragementBanner = styled.div`
  background: linear-gradient(135deg, ${ThemeColors.primary.main} 0%, ${ThemeColors.primary.dark} 100%);
  border-radius: ${Spacing.md};
  padding: ${Spacing.xl};
  margin-bottom: ${Spacing.xl};
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: pulse 3s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.1); opacity: 0.8; }
  }
`;

const EncouragementTitle = styled(Heading1)`
  color: white;
  margin-bottom: ${Spacing.sm};
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.md};
`;

const EncouragementText = styled(Text)`
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.1rem;
  margin: 0;
`;

const ContestInfoCard = styled(Card)`
  margin-bottom: ${Spacing.xl};
  background: linear-gradient(135deg, #fff5e6 0%, #fffbf0 100%);
  border: 2px solid ${ThemeColors.primary.main};
  padding: ${Spacing.xl};
`;

const ContestInfoTitle = styled(Heading2)`
  color: ${ThemeColors.primary.dark};
  margin-bottom: ${Spacing.md};
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const ContestInfoSection = styled.div`
  margin-bottom: ${Spacing.md};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContestInfoText = styled(Text)`
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};
  line-height: 1.6;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const PrizesSection = styled.div`
  background: white;
  border-radius: ${Spacing.sm};
  padding: ${Spacing.lg};
  margin-top: ${Spacing.md};
  border: 1px solid ${ThemeColors.border.light};
`;

const PrizeItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} 0;
  color: ${ThemeColors.text.primary};
  font-weight: 500;
  
  svg {
    color: ${ThemeColors.warning.main};
  }
`;

const DeadlineHighlight = styled.div`
  background: ${ThemeColors.info.light};
  border: 2px solid ${ThemeColors.info.main};
  border-radius: ${Spacing.sm};
  padding: ${Spacing.md};
  margin-top: ${Spacing.md};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  
  svg {
    color: ${ThemeColors.info.dark};
    flex-shrink: 0;
  }
  
  ${ContestInfoText}, strong {
    color: ${ThemeColors.text.primary};
  }
`;

const ContestEndedCard = styled(Card)`
  max-width: 500px;
  margin: ${Spacing.xl} auto;
  padding: ${Spacing.xl};
  text-align: center;
  background: linear-gradient(135deg, #fff5e6 0%, #fffbf0 100%);
  border: 2px solid ${ThemeColors.primary.main};
`;

const ContestEndedTitle = styled(Heading2)`
  color: ${ThemeColors.primary.dark};
  margin-bottom: ${Spacing.md};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.sm};
`;

const ContestEndedText = styled(Text)`
  color: ${ThemeColors.text.primary};
  font-size: 1.1rem;
  line-height: 1.6;
`;

const SearchCard = styled(Card)`
  margin-bottom: ${Spacing.lg};
`;

const StoreResultsContainer = styled.div`
  margin-top: ${Spacing.md};
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${Spacing.sm};
  background: ${ThemeColors.background.paper};
`;

const StoreResultItem = styled.div<{ selected?: boolean }>`
  padding: ${Spacing.md};
  border-bottom: 1px solid ${ThemeColors.border.light};
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? ThemeColors.primary.light : 'transparent'};
  
  &:hover {
    background: ${props => props.selected ? ThemeColors.primary.light : ThemeColors.gray[50]};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const StoreInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

const StoreName = styled(Text)`
  font-weight: 600;
  color: ${ThemeColors.text.primary};
  font-size: 1rem;
`;

const StoreDetails = styled(Text)`
  color: ${ThemeColors.text.secondary};
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const SelectedStoreCard = styled(Card)`
  margin-bottom: ${Spacing.lg};
  background: linear-gradient(135deg, ${ThemeColors.primary.light}15 0%, ${ThemeColors.primary.main}08 100%);
  border: 2px solid ${ThemeColors.primary.main};
`;

const PhotoUploadSection = styled(Card)`
  margin-bottom: ${Spacing.lg};
`;

const UploadArea = styled.div<{ hasImages?: boolean }>`
  border: 2px dashed ${props => props.hasImages ? ThemeColors.primary.main : ThemeColors.border.medium};
  border-radius: ${Spacing.md};
  padding: ${Spacing.xl};
  text-align: center;
  background: ${props => props.hasImages ? ThemeColors.primary.light + '20' : ThemeColors.gray[50]};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    border-color: ${ThemeColors.primary.main};
    background: ${ThemeColors.primary.light + '30'};
  }
`;

const ImagesPreviewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: ${Spacing.md};
  margin-top: ${Spacing.md};
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: ${Spacing.sm};
  }
`;

const ImagePreview = styled.div`
  position: relative;
  border-radius: ${Spacing.md};
  overflow: hidden;
  aspect-ratio: 1;
  background: ${ThemeColors.gray[100]};
  border: 2px solid ${ThemeColors.border.light};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${ThemeColors.primary.main};
    transform: scale(1.02);
  }
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${Spacing.md};
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: ${Spacing.xs};
  right: ${Spacing.xs};
  background: ${ThemeColors.error.main};
  color: white;
  border: 2px solid white;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover {
    background: ${ThemeColors.error.dark};
    transform: scale(1.15);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const SubmitButton = styled(Button)`
  background: linear-gradient(135deg, ${ThemeColors.primary.main} 0%, ${ThemeColors.primary.dark} 100%);
  padding: ${Spacing.md} ${Spacing.xl};
  font-size: 1.1rem;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(160, 201, 63, 0.3);
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  width: 100%;
  justify-content: center;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(160, 201, 63, 0.4);
  }
  
  &:disabled {
    transform: none;
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background: ${ThemeColors.success.light};
  border: 2px solid ${ThemeColors.success.main};
  border-radius: ${Spacing.md};
  padding: ${Spacing.lg};
  margin-bottom: ${Spacing.lg};
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  color: ${ThemeColors.success.dark};
`;

const ViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg};
`;

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.lg};
`;

const NewEntryButton = styled(Button)`
  background: linear-gradient(135deg, ${ThemeColors.primary.main} 0%, ${ThemeColors.primary.dark} 100%);
  padding: ${Spacing.md} ${Spacing.xl};
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  box-shadow: 0 4px 12px rgba(160, 201, 63, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(160, 201, 63, 0.4);
  }
`;

const EntriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${Spacing.lg};
`;

const EntryCard = styled(Card)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const EntryImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  background: ${ThemeColors.gray[100]};
  cursor: pointer;
`;

const EntryContent = styled.div`
  padding: ${Spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm};
`;

const EntryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const EntryTitle = styled(Text)`
  font-weight: 600;
  font-size: 1.1rem;
  color: ${ThemeColors.text.primary};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => {
    switch (props.status) {
      case 'pending':
        return `
          background: ${ThemeColors.warning.light};
          color: ${ThemeColors.warning.dark};
        `;
      case 'reviewed':
        return `
          background: ${ThemeColors.info.light};
          color: ${ThemeColors.info.dark};
        `;
      case 'approved':
        return `
          background: ${ThemeColors.success.light};
          color: ${ThemeColors.success.dark};
        `;
      case 'rejected':
        return `
          background: ${ThemeColors.error.light};
          color: ${ThemeColors.error.dark};
        `;
      default:
        return `
          background: ${ThemeColors.gray[100]};
          color: ${ThemeColors.gray[600]};
        `;
    }
  }}
`;

const EntryInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  font-size: 0.875rem;
  color: ${ThemeColors.text.secondary};
`;

const EntryMessage = styled(Text)`
  font-size: 0.875rem;
  color: ${ThemeColors.text.primary};
  font-style: italic;
  padding: ${Spacing.sm};
  background: ${ThemeColors.gray[50]};
  border-radius: ${Spacing.xs};
  margin-top: ${Spacing.xs};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.xxl};
  color: ${ThemeColors.text.secondary};
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${Spacing.lg};
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${Spacing.md};
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  width: 100%;
  position: relative;
`;

const ModalImageContainer = styled.div`
  position: relative;
  width: 100%;
  background: ${ThemeColors.gray[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

const ModalImage = styled.img`
  width: 100%;
  max-height: 60vh;
  object-fit: contain;
  background: ${ThemeColors.gray[100]};
`;

const ModalNavButton = styled.button<{ position: 'left' | 'right' }>`
  position: absolute;
  ${props => props.position}: ${Spacing.md};
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid ${ThemeColors.primary.main};
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover {
    background: ${ThemeColors.primary.main};
    color: white;
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    &:hover {
      transform: translateY(-50%);
      background: rgba(255, 255, 255, 0.9);
      color: inherit;
    }
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const ModalHeader = styled.div`
  padding: ${Spacing.lg};
  border-bottom: 1px solid ${ThemeColors.border.light};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  background: white;
  z-index: 1;
`;

const ModalClose = styled.button`
  position: absolute;
  top: ${Spacing.xl};
  right: ${Spacing.xl};
  background: ${ThemeColors.error.main};
  color: white;
  border: 2px solid white;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  transition: all 0.2s ease;
  z-index: 20;
  
  &:hover {
    background: ${ThemeColors.error.dark};
    transform: scale(1.15);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
  }
  
  svg {
    width: 22px;
    height: 22px;
  }
`;

const formatDate = (date: Date | any): string => {
  if (!date) return 'N/A';
  const d = date.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface Store {
  id: string;
  CustomerNo: string;
  StoreName: string;
  Address: string;
  City: string;
  Prov?: string;
  PostalCode?: string;
  Telephone?: string;
}

export const ContestEntryScreen: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const fileStorageService = FileStorageService.getInstance();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [viewMode, setViewMode] = useState<'view' | 'submit'>('view');
  const [entries, setEntries] = useState<ContestEntry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<ContestEntry | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [stores, setStores] = useState<Store[]>([]);
  const [filteredStores, setFilteredStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contestEnabled, setContestEnabled] = useState(true);
  const [loadingContestSettings, setLoadingContestSettings] = useState(true);

  // Load contest enabled state on mount
  useEffect(() => {
    const loadContestSettings = async () => {
      try {
        setLoadingContestSettings(true);
        const enabled = await ContestEntryService.getContestEnabled();
        setContestEnabled(enabled);
      } catch (error) {
        console.error('Error loading contest settings:', error);
        setContestEnabled(true); // Default to enabled on error
      } finally {
        setLoadingContestSettings(false);
      }
    };
    loadContestSettings();
  }, []);

  // Load entries when in view mode
  useEffect(() => {
    if (viewMode === 'view' && user) {
      loadEntries();
    }
  }, [viewMode, user]);

  // Load all stores on mount
  useEffect(() => {
    loadStores();
  }, []);

  const loadEntries = async () => {
    if (!user) return;
    
    try {
      setLoadingEntries(true);
      const allEntries = await ContestEntryService.getAllEntries(
        user.id,
        'sales-representative'
      );
      // Sort by submission date, newest first
      const sorted = allEntries.sort((a, b) => {
        const dateA = a.submittedAt instanceof Date ? a.submittedAt : (a.submittedAt as any)?.toDate?.() || new Date();
        const dateB = b.submittedAt instanceof Date ? b.submittedAt : (b.submittedAt as any)?.toDate?.() || new Date();
        return dateB.getTime() - dateA.getTime();
      });
      setEntries(sorted);
    } catch (error: any) {
      console.error('Error loading entries:', error);
      toast.error('Failed to load your entries');
    } finally {
      setLoadingEntries(false);
    }
  };

  const loadStores = async () => {
    try {
      const storesRef = collection(firestore, 'Stores');
      const querySnapshot = await getDocs(storesRef);
      
      const storesData: Store[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        storesData.push({
          id: doc.id,
          CustomerNo: data.CustomerNo || '',
          StoreName: data.StoreName || 'Unknown Store',
          Address: data.Address || '',
          City: data.City || '',
          Prov: data.Prov || '',
          PostalCode: data.PostalCode || '',
          Telephone: data.Telephone || '',
        });
      });
      
      setStores(storesData);
    } catch (error) {
      console.error('Error loading stores:', error);
      toast.error(t('contestEntry.loadStoresError', 'Failed to load stores. Please try again.'));
    }
  };

  // Filter stores based on search query
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filtered = stores.filter(store => 
        store.CustomerNo?.toLowerCase().includes(query) ||
        store.StoreName?.toLowerCase().includes(query) ||
        store.Address?.toLowerCase().includes(query) ||
        store.City?.toLowerCase().includes(query) ||
        `${store.City} ${store.Prov}`.toLowerCase().includes(query)
      );
      setFilteredStores(filtered);
      setShowResults(filtered.length > 0);
    } else {
      setFilteredStores([]);
      setShowResults(false);
    }
  }, [searchQuery, stores]);

  const handleStoreSelect = (store: Store) => {
    setSelectedStore(store);
    setSearchQuery('');
    setShowResults(false);
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles: File[] = [];
    const previews: string[] = [];

    for (const file of files) {
      // Validate file
      const validation = fileStorageService.validateFile(file, 10); // 10MB limit
      if (!validation.valid) {
        toast.error(`${file.name}: ${validation.error || 'Invalid file'}`);
        continue;
      }

      // Check if it's an image
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name}: Please select an image file`);
        continue;
      }

      validFiles.push(file);

      // Create preview
      const reader = new FileReader();
      await new Promise<void>((resolve) => {
        reader.onloadend = () => {
          previews.push(reader.result as string);
          resolve();
        };
        reader.readAsDataURL(file);
      });
    }

    if (validFiles.length > 0) {
      setSelectedImages(prev => [...prev, ...validFiles]);
      setImagePreviews(prev => [...prev, ...previews]);
      toast.success(`Added ${validFiles.length} image(s)`);
    }

    // Reset input to allow selecting the same files again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleCameraCapture = () => {
    // Trigger camera capture by clicking the file input
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSubmit = async () => {
    if (!selectedStore) {
      toast.error(t('contestEntry.selectStoreFirst', 'Please select a store first'));
      return;
    }

    if (selectedImages.length === 0) {
      toast.error(t('contestEntry.uploadPhotoFirst', 'Please upload at least one photo of the display'));
      return;
    }

    if (!user) {
      toast.error(t('contestEntry.mustBeLoggedIn', 'You must be logged in to submit'));
      return;
    }

    setSubmitting(true);

    try {
      const imageDirectory = `contest-entries/${user.id}`;

      // Upload originals without re-encoding. Canvas "lossless PNG" conversion
      // of phone JPEGs expands files ~3–5x; keeping camera bytes is both
      // smaller and truly lossless relative to what the rep shot.
      const uploadPromises = selectedImages.map(file =>
        fileStorageService.uploadFile(
          file,
          imageDirectory,
          user.email || user.id
        )
      );

      const uploadResults = await Promise.all(uploadPromises);

      // Create contest entry documents for each image
      const entryPromises = uploadResults.map(uploadResult => {
        const contestEntry = {
          storeId: selectedStore.id,
          storeName: selectedStore.StoreName,
          storeAddress: selectedStore.Address,
          storeCity: selectedStore.City,
          storeProvince: selectedStore.Prov || '',
          storeCustomerNo: selectedStore.CustomerNo,
          imageUrl: uploadResult.url,
          imagePath: uploadResult.path,
          message: message.trim() || null,
          submittedBy: user.id,
          submittedByName: user.username || user.email || 'Unknown',
          submittedAt: Timestamp.now(),
          status: 'pending',
        };

        return addDoc(collection(firestore, 'contest-entries'), contestEntry);
      });

      await Promise.all(entryPromises);

      toast.success(
        t('contestEntry.submissionSuccess', '🎉 {count} contest entry(ies) submitted successfully! Good luck!', { count: uploadResults.length })
      );
      setSubmitted(true);

      // Reset form and reload entries
      setSelectedStore(null);
      setSelectedImages([]);
      setImagePreviews([]);
      setMessage('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Reload entries after submission
      setTimeout(async () => {
        setSubmitted(false);
        await loadEntries();
        setViewMode('view');
      }, 2000);

    } catch (error: any) {
      console.error('Error submitting contest entry:', error);
      toast.error(`${t('contestEntry.submissionError', 'Failed to submit entry')}: ${error.message || t('common.pleaseTryAgain', 'Please try again')}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading contest settings - show brief loading state
  if (loadingContestSettings) {
    return (
      <PageContainer isMenuCollapsed={false}>
        <MainContent>
          <Toaster position="top-right" />
          <ContestEndedCard>
            <Text>{t('common.loading', 'Loading...')}</Text>
          </ContestEndedCard>
        </MainContent>
      </PageContainer>
    );
  }

  // Contest disabled - show "contest ended" message
  if (!contestEnabled) {
    return (
      <PageContainer isMenuCollapsed={false}>
        <MainContent>
          <Toaster position="top-right" />
          <ContestEndedCard>
            <ContestEndedTitle>
              <Trophy size={32} />
              {t('contestEntry.contestEnded', 'Contest Ended')}
            </ContestEndedTitle>
            <ContestEndedText>
              {t('contestEntry.contestEndedMessage', 'The contest ended for now. We will let you know when the next contest opens.')}
            </ContestEndedText>
            <div style={{ marginTop: Spacing.lg, display: 'flex', justifyContent: 'center' }}>
              <Bell size={24} color={ThemeColors.primary.main} style={{ opacity: 0.7 }} />
            </div>
          </ContestEndedCard>
        </MainContent>
      </PageContainer>
    );
  }

  // View mode - show submitted entries
  if (viewMode === 'view') {
    return (
      <PageContainer isMenuCollapsed={false}>
        <MainContent>
          <Toaster position="top-right" />
          <HeaderActions>
            <Heading2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: Spacing.sm, fontSize: '1.5rem' }}>
              <Trophy size={24} />
              {t('contestEntry.myEntries', 'My Contest Entries')}
            </Heading2>
            <NewEntryButton onClick={() => setViewMode('submit')}>
              <Plus size={20} />
              {t('contestEntry.submitNewEntry', 'Submit a New Entry')}
            </NewEntryButton>
          </HeaderActions>

          {/* Contest Information Card */}
          <ContestInfoCard>
            <ContestInfoTitle>
              <Trophy size={28} />
              Contest: Best Setups – All banners
            </ContestInfoTitle>
            
            <ContestInfoSection>
              <ContestInfoText>
                Submissions should clearly showcase McKenzie across all product lines and reflect a strong, consistent brand presence in a prime location.
              </ContestInfoText>
            </ContestInfoSection>

            <ContestInfoSection>
              <ContestInfoText>
                <strong>Please only submit one picture per account.</strong> You can submit as many accounts as you choose.
              </ContestInfoText>
            </ContestInfoSection>

            <ContestInfoSection>
              <ContestInfoText>
                <strong>The more displays together in the picture the better!</strong>
              </ContestInfoText>
              <ContestInfoText style={{ marginLeft: 4, fontSize: '0.95rem' }}>
                (Seed display/Wildflower bags + Jiffy + Soil + Accessories + Live Goods)
              </ContestInfoText>
            </ContestInfoSection>

            <DeadlineHighlight>
              <Clock size={24} />
              <ContestInfoText style={{ margin: 0 }}>
                <strong>Submission deadline: February 15th, 2026</strong>
              </ContestInfoText>
            </DeadlineHighlight>

            <PrizesSection>
              <ContestInfoText style={{ fontWeight: 600, fontSize: '1.1rem', marginBottom: Spacing.md, color: ThemeColors.primary.dark }}>
                🏆 Prizes
              </ContestInfoText>
              <PrizeItem>
                <Star size={20} fill={ThemeColors.warning.main} />
                <strong>GRAND PRIZE:</strong> $150 gift card
              </PrizeItem>
              <PrizeItem>
                <Star size={18} fill={ThemeColors.warning.main} />
                <strong>2 SECONDARY PRIZES:</strong> $50 gift card
              </PrizeItem>
              <PrizeItem>
                <Star size={16} fill={ThemeColors.warning.main} />
                <strong>5 HONOURABLE MENTIONS:</strong> $25 gift card
              </PrizeItem>
            </PrizesSection>

            <ContestInfoSection style={{ marginTop: Spacing.md }}>
              <ContestInfoText style={{ textAlign: 'right', fontStyle: 'italic', color: ThemeColors.text.secondary }}>
           
              </ContestInfoText>
            </ContestInfoSection>
          </ContestInfoCard>

          {loadingEntries ? (
            <EmptyState>
              <Text>{t('contestEntry.loadingEntries', 'Loading your entries...')}</Text>
            </EmptyState>
          ) : entries.length === 0 ? (
            <EmptyState>
              <Trophy size={48} style={{ marginBottom: Spacing.md, opacity: 0.5 }} />
              <Heading2>{t('contestEntry.noEntriesYet', 'No entries yet')}</Heading2>
              <Text style={{ marginTop: Spacing.md }}>
                {t('contestEntry.noEntriesMessage', "You haven't submitted any contest entries yet.")}
              </Text>
              <NewEntryButton 
                onClick={() => setViewMode('submit')}
                style={{ marginTop: Spacing.lg, width: 'auto', display: 'inline-flex' }}
              >
                <Plus size={20} />
                {t('contestEntry.submitFirstEntry', 'Submit Your First Entry')}
              </NewEntryButton>
            </EmptyState>
          ) : (
            <EntriesGrid>
              {entries.map((entry) => (
                <EntryCard key={entry.id}>
                  <EntryImage
                    src={entry.imageUrl}
                    alt={entry.storeName}
                    onClick={() => setSelectedEntry(entry)}
                  />
                  <EntryContent>
                    <EntryHeader>
                      <EntryTitle>
                        <Store size={18} />
                        {entry.storeName}
                      </EntryTitle>
                      {/* Hide status badge for sales reps */}
                    </EntryHeader>
                    <EntryInfo>
                      <Text style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs }}>
                        <MapPin size={14} />
                        {entry.storeCity}, {entry.storeProvince || ''}
                      </Text>
                      <Text style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs }}>
                        <Calendar size={14} />
                        {formatDate(entry.submittedAt)}
                      </Text>
                      {/* Hide rating for sales reps */}
                    </EntryInfo>
                    {entry.message && (
                      <EntryMessage>{entry.message}</EntryMessage>
                    )}
                  </EntryContent>
                </EntryCard>
              ))}
            </EntriesGrid>
          )}

          {/* Image View Modal */}
          {selectedEntry && (
            <Modal isOpen={!!selectedEntry} onClick={() => {
              setSelectedEntry(null);
              setCurrentImageIndex(0);
            }}>
              <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalClose onClick={() => {
                  setSelectedEntry(null);
                  setCurrentImageIndex(0);
                }}>
                  <X size={22} />
                </ModalClose>
                <ModalHeader>
                  <Heading2 style={{ margin: 0, paddingRight: '60px' }}>{selectedEntry.storeName}</Heading2>
                </ModalHeader>
                <ModalImageContainer>
                  <ModalImage 
                    src={selectedEntry.imageUrl} 
                    alt={selectedEntry.storeName}
                  />
                  {/* Navigation arrows - show if user has multiple entries */}
                  {entries.length > 1 && (
                    <>
                      <ModalNavButton
                        position="left"
                        onClick={(e) => {
                          e.stopPropagation();
                          const currentIdx = entries.findIndex(e => e.id === selectedEntry.id);
                          const prevIdx = currentIdx > 0 ? currentIdx - 1 : entries.length - 1;
                          setSelectedEntry(entries[prevIdx]);
                        }}
                      >
                        <ChevronLeft />
                      </ModalNavButton>
                      <ModalNavButton
                        position="right"
                        onClick={(e) => {
                          e.stopPropagation();
                          const currentIdx = entries.findIndex(e => e.id === selectedEntry.id);
                          const nextIdx = currentIdx < entries.length - 1 ? currentIdx + 1 : 0;
                          setSelectedEntry(entries[nextIdx]);
                        }}
                      >
                        <ChevronRight />
                      </ModalNavButton>
                    </>
                  )}
                </ModalImageContainer>
                <div style={{ padding: Spacing.lg }}>
                  <EntryInfo style={{ gap: Spacing.sm }}>
                    <Text><strong>{t('contestEntry.store', 'Store')}:</strong> {selectedEntry.storeName}</Text>
                    <Text><strong>{t('contestEntry.location', 'Location')}:</strong> {selectedEntry.storeAddress}, {selectedEntry.storeCity}, {selectedEntry.storeProvince || ''}</Text>
                    <Text><strong>{t('contestEntry.customerNumber', 'Customer #')}:</strong> {selectedEntry.storeCustomerNo}</Text>
                    <Text><strong>{t('contestEntry.submitted', 'Submitted')}:</strong> {formatDate(selectedEntry.submittedAt)}</Text>
                    {/* Hide status and rating for sales reps */}
                    {selectedEntry.notes && (
                      <Text><strong>{t('contestEntry.notes', 'Notes')}:</strong> {selectedEntry.notes}</Text>
                    )}
                  </EntryInfo>
                  {selectedEntry.message && (
                    <EntryMessage style={{ marginTop: Spacing.md }}>{selectedEntry.message}</EntryMessage>
                  )}
                </div>
              </ModalContent>
            </Modal>
          )}
        </MainContent>
      </PageContainer>
    );
  }

  // Submit mode - show submission form
  return (
    <PageContainer isMenuCollapsed={false}>
      <MainContent>
        <Toaster position="top-right" />
        <HeaderActions>
          <Heading1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: Spacing.sm }}>
            <Trophy size={32} />
            {t('contestEntry.title', 'Best Display Contest')}
          </Heading1>
          <Button onClick={() => setViewMode('view')}>
            {t('contestEntry.viewMyEntries', 'View My Entries')}
          </Button>
        </HeaderActions>

        {submitted ? (
          <SuccessMessage>
            <CheckCircle size={32} />
            <div>
              <Heading2 style={{ margin: 0, color: ThemeColors.success.dark }}>
                {t('contestEntry.entrySubmitted', 'Entry Submitted Successfully! 🎉')}
              </Heading2>
              <Text style={{ marginTop: Spacing.xs }}>
                {t('contestEntry.entrySubmittedMessage', 'Thank you for your submission! Your entry has been received and will be reviewed.')}
              </Text>
            </div>
          </SuccessMessage>
        ) : (
          <>
            <EncouragementBanner>
              <EncouragementTitle>
                <Trophy size={40} />
                {t('contestEntry.title', 'Best Display Contest')}
                <Sparkles size={40} />
              </EncouragementTitle>
              <EncouragementText>
                {t('contestEntry.subtitle', 'Show off your amazing store displays! Upload a photo of your best presentation and share why it deserves to win.')}
              </EncouragementText>
            </EncouragementBanner>

            <ContestInfoCard>
              <ContestInfoTitle>
                <Trophy size={28} />
                Contest: Best Setups – All banners
              </ContestInfoTitle>
              
              <ContestInfoSection>
                <ContestInfoText>
                  Submissions should clearly showcase McKenzie across all product lines and reflect a strong, consistent brand presence in a prime location.
                </ContestInfoText>
              </ContestInfoSection>

              <ContestInfoSection>
                <ContestInfoText>
                  <strong>Please only submit one picture per account.</strong> You can submit as many accounts as you choose.
                </ContestInfoText>
              </ContestInfoSection>

              <ContestInfoSection>
                <ContestInfoText>
                  <strong>The more displays together in the picture the better!</strong>
                </ContestInfoText>
                <ContestInfoText style={{ fontSize: '0.95rem' }}>
                  (Seed display/Wildflower bags + Jiffy + Soil + Accessories + Live Goods)
                </ContestInfoText>
              </ContestInfoSection>

              <DeadlineHighlight>
                <Clock size={24} />
                <ContestInfoText style={{ margin: 0 }}>
                  <strong>Submission deadline: February 15th, 2026</strong>
                </ContestInfoText>
              </DeadlineHighlight>

              <PrizesSection>
                <ContestInfoText style={{ fontWeight: 600, fontSize: '1.1rem', marginBottom: Spacing.md, color: ThemeColors.primary.dark }}>
                  🏆 Prizes
                </ContestInfoText>
                <PrizeItem>
                  <Star size={20} fill={ThemeColors.warning.main} />
                  <strong>GRAND PRIZE:</strong> $150 gift card
                </PrizeItem>
                <PrizeItem>
                  <Star size={18} fill={ThemeColors.warning.main} />
                  <strong>2 SECONDARY PRIZES:</strong> $50 gift card
                </PrizeItem>
                <PrizeItem>
                  <Star size={16} fill={ThemeColors.warning.main} />
                  <strong>5 HONOURABLE MENTIONS:</strong> $25 gift card
                </PrizeItem>
              </PrizesSection>

              <ContestInfoSection style={{ marginTop: Spacing.md }}>
                <ContestInfoText style={{ textAlign: 'right', fontStyle: 'italic', color: ThemeColors.text.secondary }}>
                  Thanks,
                </ContestInfoText>
              </ContestInfoSection>
            </ContestInfoCard>

        <SearchCard>
          <Heading2 style={{ marginBottom: Spacing.md, display: 'flex', alignItems: 'center', gap: Spacing.sm }}>
            <Search size={24} />
            {t('contestEntry.searchForStore', 'Search for a Store')}
          </Heading2>
          <FormGroup>
            <FormLabel>{t('contestEntry.searchBy', 'Search by Customer Number, Store Name, Address, or City')}</FormLabel>
            <FlexRow style={{ position: 'relative' }}>
              <Input
                type="text"
                placeholder={t('contestEntry.searchPlaceholder', 'Type to search for a store...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => {
                  if (filteredStores.length > 0) setShowResults(true);
                }}
                style={{ flex: 1 }}
              />
              {showResults && filteredStores.length > 0 && (
                <StoreResultsContainer style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, marginTop: Spacing.xs }}>
                  {filteredStores.slice(0, 5).map((store) => (
                    <StoreResultItem
                      key={store.id}
                      onClick={() => handleStoreSelect(store)}
                    >
                      <StoreInfo>
                        <StoreName>
                          <Store size={16} style={{ display: 'inline', marginRight: Spacing.xs }} />
                          {store.StoreName}
                        </StoreName>
                        <StoreDetails>
                          <Text style={{ fontSize: '0.875rem' }}>
                            <strong>{t('contestEntry.customerNumber', 'Customer Number')}:</strong> {store.CustomerNo}
                          </Text>
                        </StoreDetails>
                        <StoreDetails>
                          <MapPin size={14} />
                          {store.Address}, {store.City}{store.Prov ? `, ${store.Prov}` : ''} {store.PostalCode || ''}
                        </StoreDetails>
                      </StoreInfo>
                    </StoreResultItem>
                  ))}
                </StoreResultsContainer>
              )}
            </FlexRow>
          </FormGroup>
        </SearchCard>

        {selectedStore && (
          <SelectedStoreCard>
            <Heading2 style={{ marginBottom: Spacing.md, color: ThemeColors.primary.dark }}>
              {t('contestEntry.selectedStore', 'Selected Store')}
            </Heading2>
            <FlexColumn>
              <StoreName style={{ fontSize: '1.2rem', marginBottom: Spacing.xs }}>
                <Store size={20} style={{ display: 'inline', marginRight: Spacing.xs, verticalAlign: 'middle' }} />
                {selectedStore.StoreName}
              </StoreName>
              <StoreDetails style={{ marginBottom: Spacing.xs }}>
                <strong>{t('contestEntry.customerNumber', 'Customer Number')}:</strong> {selectedStore.CustomerNo}
              </StoreDetails>
              <StoreDetails>
                <MapPin size={16} />
                {selectedStore.Address}, {selectedStore.City}{selectedStore.Prov ? `, ${selectedStore.Prov}` : ''} {selectedStore.PostalCode || ''}
              </StoreDetails>
            </FlexColumn>
          </SelectedStoreCard>
        )}

        {selectedStore && (
          <PhotoUploadSection>
            <Heading2 style={{ marginBottom: Spacing.md, display: 'flex', alignItems: 'center', gap: Spacing.sm, fontSize: '1.1rem' }}>
              <Camera size={20} />
              {t('contestEntry.uploadDisplayPhoto', 'Upload Display Photo')}
            </Heading2>
            <FormGroup>
              <FormLabel>{t('contestEntry.uploadPhotoLabel', 'Upload photos of your best display presentation (you can select multiple)')}</FormLabel>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                style={{ display: 'none' }}
              />
              <UploadArea
                hasImages={imagePreviews.length > 0}
                onClick={() => fileInputRef.current?.click()}
              >
                <FlexColumn style={{ alignItems: 'center', gap: Spacing.md }}>
                  <Upload size={48} color={ThemeColors.primary.main} />
                  <Text style={{ fontWeight: 500 }}>
                    {t('contestEntry.clickToUpload', 'Click to upload photos or take pictures')}
                  </Text>
                  <Text style={{ fontSize: '0.875rem', color: ThemeColors.text.secondary }}>
                    {t('contestEntry.uploadFormat', 'PNG, JPG, GIF up to 10MB each')}
                  </Text>
                  {imagePreviews.length > 0 && (
                    <Text style={{ fontSize: '0.875rem', color: ThemeColors.primary.main, fontWeight: 500 }}>
                      {imagePreviews.length} photo(s) selected - Click to add more
                    </Text>
                  )}
                </FlexColumn>
              </UploadArea>
              
              {imagePreviews.length > 0 && (
                <ImagesPreviewContainer>
                  {imagePreviews.map((preview, index) => (
                    <ImagePreview key={index}>
                      <PreviewImage src={preview} alt={`Preview ${index + 1}`} />
                      <RemoveImageButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage(index);
                        }}
                        title="Remove image"
                      >
                        <X size={16} />
                      </RemoveImageButton>
                    </ImagePreview>
                  ))}
                </ImagesPreviewContainer>
              )}
            </FormGroup>

            <FormGroup>
              <FormLabel>{t('contestEntry.optionalMessage', 'Optional Message')}</FormLabel>
              <TextArea
                placeholder={t('contestEntry.optionalMessagePlaceholder', 'Tell us why this display deserves to win! (Optional)')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                style={{ minHeight: '100px' }}
              />
              <Text style={{ fontSize: '0.875rem', color: ThemeColors.text.secondary, marginTop: Spacing.xs }}>
                {t('contestEntry.optionalMessageHint', "Share what makes this display special or any details you'd like the judges to know.")}
              </Text>
            </FormGroup>

            <Spacer height={Spacing.lg} />

            <SubmitButton
              onClick={handleSubmit}
              disabled={submitting || selectedImages.length === 0}
            >
              {submitting ? (
                <>
                  <Text>{t('contestEntry.submitting', 'Submitting...')}</Text>
                </>
              ) : (
                <>
                  <Trophy size={20} />
                  {t('contestEntry.submitEntry', 'Submit Contest Entry')}
                </>
              )}
            </SubmitButton>
          </PhotoUploadSection>
        )}
          </>
        )}
      </MainContent>
    </PageContainer>
  );
};

